import React, { useState, useEffect } from 'react';
import ImageLoadingAnimation from './ImageLoadingAnimation';
import { Theme, themeColors } from './types';
import { MessageWithCitations } from './chatTypes';

interface UserMessageWithImageProps {
  message: MessageWithCitations;
  theme: Theme;
  messageStyle: any;
  retrieveFile: (filename: string) => Promise<File | undefined>;
  copiedMessageId: string | null;
  setCopiedMessageId: (id: string | null) => void;
}

const UserMessageWithImage: React.FC<UserMessageWithImageProps> = ({ 
  message, 
  theme, 
  messageStyle, 
  retrieveFile,
  copiedMessageId,
  setCopiedMessageId
}) => {
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | null>(null);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadImages = async () => {
      // Handle multiple files if available
      if (message.fileUrls && message.fileUrls.length > 0) {
        setIsLoading(true);
        const urls: string[] = [];
        
        try {
          for (const fileUrl of message.fileUrls) {
            if (fileUrl.startsWith('blob:')) {
              urls.push(fileUrl);
            } else {
              // Load from IndexedDB if needed
              const file = await retrieveFile(fileUrl);
              if (file) {
                const url = URL.createObjectURL(file);
                urls.push(url);
              }
            }
          }
          
          if (isMounted) {
            setReferenceImageUrls(urls);
            setError(urls.length === 0);
          }
        } catch (err) {
          console.error('UserMessageWithImage: Error loading images:', err);
          if (isMounted) setError(true);
        } finally {
          if (isMounted) setIsLoading(false);
        }
        return;
      }

      // Fallback to single file for backward compatibility
      if (!message.fileUrl) return;

      try {
        setIsLoading(true);
        console.log('UserMessageWithImage: Attempting to load image:', message.fileUrl);

        // If it's a blob URL, use it directly
        if (message.fileUrl.startsWith('blob:')) {
          console.log('UserMessageWithImage: Using blob URL directly');
          setReferenceImageUrl(message.fileUrl);
          setError(false);
        } else {
          // Otherwise load from IndexedDB
          const file = await retrieveFile(message.fileUrl);
          console.log('UserMessageWithImage: Retrieved file:', file ? {
            name: file.name,
            type: file.type,
            size: file.size
          } : 'Not found');

          if (file && isMounted) {
            const url = URL.createObjectURL(file);
            console.log('UserMessageWithImage: Created object URL:', url);
            setReferenceImageUrl(url);
            setError(false);
          } else if (isMounted) {
            console.error('UserMessageWithImage: Image file not found:', message.fileUrl);
            setError(true);
          }
        }
      } catch (err) {
        console.error('UserMessageWithImage: Error loading image:', err);
        if (isMounted) setError(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadImages();

    return () => {
      isMounted = false;
      // Clean up single image URL
      if (referenceImageUrl && !message.fileUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(referenceImageUrl);
      }
      // Clean up multiple image URLs
      referenceImageUrls.forEach(url => {
        if (!message.fileUrls?.some(fileUrl => fileUrl.startsWith('blob:') && fileUrl === url)) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [message.fileUrl, message.fileUrls, retrieveFile]);

  return (
    <div style={{
      padding: '20px',
      ...messageStyle
    }}>
      <div style={{ whiteSpace: 'pre-wrap' }}>
        {message.content.includes('[File:') ? (
          <>
            {message.content.split('[File:')[0]}
            {isLoading ? (
              <ImageLoadingAnimation theme={theme} />
            ) : error ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: theme === 'light' ? '#991B1B' : '#FCA5A5',
                backgroundColor: theme === 'light' ? '#FEF2F2' : '#2D1F1F',
                borderRadius: '8px'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin: '0 auto 16px', display: 'block' }}>
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <div>Failed to load image</div>
              </div>
            ) : (referenceImageUrls.length > 0 || referenceImageUrl) && (
              <div style={{ 
                maxWidth: '400px', 
                margin: '10px 0',
                borderRadius: '8px',
                overflow: 'hidden',
                border: `1px solid ${themeColors[theme].border}`,
                backgroundColor: themeColors[theme].messageBackground.user
              }}>
                <div style={{
                  padding: '8px 12px',
                  borderBottom: `1px solid ${themeColors[theme].border}`,
                  fontSize: '12px',
                  color: themeColors[theme].secondaryText
                }}>
                  {referenceImageUrls.length > 1 ? `Reference Images (${referenceImageUrls.length})` : 'Reference Image'}
                </div>
                <div style={{ padding: '12px' }}>
                  {referenceImageUrls.length > 0 ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: referenceImageUrls.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '8px'
                    }}>
                      {referenceImageUrls.map((url, index) => (
                        <img 
                          key={index}
                          src={url}
                          alt={`Reference image ${index + 1}`}
                          style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            borderRadius: '4px'
                          }}
                        />
                      ))}
                    </div>
                  ) : referenceImageUrl && (
                    <img 
                      src={referenceImageUrl}
                      alt="Reference image"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '4px'
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          message.content
        )}
      </div>

      {/* Copy button for user messages */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: '16px',
        paddingTop: '12px',
        justifyContent: 'flex-start'
      }}>
        <button 
          className="message-action-btn"
          onClick={() => {
            navigator.clipboard.writeText(message.content);
            const messageId = message.created_at || String(Math.random());
            setCopiedMessageId(messageId);
            setTimeout(() => setCopiedMessageId(null), 1000);
          }}
          style={{
            color: copiedMessageId === (message.created_at || String(Math.random())) ? '#10A37F' : 'inherit'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default UserMessageWithImage;